import { inject, Injectable } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { TranslocoService } from '@jsverse/transloco';
import { Navigation } from 'app/core/navigation/navigation.types';
import { isSystemOrg } from 'app/core/constants/system-org.constants';
import {
    hasAnyRole,
    ORG_ADMIN_AREA_ROLES,
    PLATFORM_STAFF_ROLES,
} from 'app/shared/constants';
import { Observable, of, ReplaySubject, switchMap } from 'rxjs';
import { AuthService } from '../services/auth.service';
import { FeatureFlagService } from '../services/feature-flag.service';
import {
    buildPhotoflowAdminFallbackNav,
    buildPhotoflowOrgFallbackNav,
    filterNavByFeatureFlags,
    localizeNavTitles,
} from './fallback-navigation';
import { buildPhotoQaCentralFallbackNav } from './photoqa-fallback-navigation';

/**
 * Builds sidebar navigation client-side from the static role-gated trees in
 * `fallback-navigation.ts` (mirrors BE `PhotoFlowMenuSeedData`).
 *
 * There is no menu-service backend anymore — navigation is generated in the
 * FE, same as every other portal (nera-iam-portal, nera-sa-portal).
 *
 * Resource selection:
 * - pure SUPER_ADMIN (no ADMIN hat) → photoflow-admin tree
 * - everyone else → photoflow-org + photoqa-central trees, merged
 */
@Injectable({ providedIn: 'root' })
export class NavigationService {
    private readonly _authService = inject(AuthService);
    private readonly _transloco = inject(TranslocoService);
    private readonly _flags = inject(FeatureFlagService);
    private readonly _navigation = new ReplaySubject<Navigation>(1);

    constructor() {
        // kanban #221: nav titles are translated once (a `translate()` snapshot,
        // not the reactive `transloco` pipe/directive main content uses), so a
        // live language switch left the sidebar stuck on the old language until
        // a full reload. Rebuild on every langChanges$ emission.
        //
        // langChanges$ fires as soon as the switch STARTS, before the target
        // language's translation file has necessarily loaded — calling
        // `translate()` at that instant returns the raw key (e.g.
        // "nav.fallback.work") instead of falling back to the old value, which
        // is worse than the original bug. `load(lang)` is the translations
        // themselves (cached instantly on a lang already used this session,
        // awaited once per lang otherwise), so switchMap into it before
        // rebuilding guarantees `get()` only runs once the new dictionary is
        // actually in place. The first emission race with app.resolvers.ts's
        // own `get()` call is harmless: this is a ReplaySubject(1), so
        // whichever call lands last wins, and both compute the same tree once
        // auth state settles.
        this._transloco.langChanges$
            .pipe(switchMap((lang) => this._transloco.load(lang)))
            .subscribe(() => this.get());
        // Unleash flags start at their FEATURE_PFL_* bootstrap (so the first
        // build already hides what the env turns off) and then follow Unleash
        // (polled every minute): rebuild so a flag flip shows/hides gated
        // items without a reload.
        toObservable(this._flags.flags).subscribe(() => this.get());
    }

    get navigation$(): Observable<Navigation> {
        return this._navigation.asObservable();
    }

    /** Build (or rebuild) navigation for the current user. */
    get(): Observable<Navigation> {
        const user = this._authService.user;
        const userRoles = user?.roles;
        // "Pure platform staff, no org hat" accepts SUPER_ADMIN or
        // PLATFORM_ADMIN (both never carry an org tier, design §2.9); "has
        // an org-admin hat" accepts ORG_OWNER/ORG_OPERATOR.
        const isPureSuperAdmin =
            hasAnyRole(userRoles, [...PLATFORM_STAFF_ROLES]) &&
            !hasAnyRole(userRoles, [...ORG_ADMIN_AREA_ROLES]);
        // PFL-2110: PhotoQA admin-config group is platform-managed — a global-org admin
        // (platform staff, or an org-admin-tier holder whose ACTING org is the shared
        // System org) sees it; customer-org admins do not. Mirrors GlobalOrgAdminGuard +
        // the backend RequireGlobalOrgAdmin policy. `user.isAdmin` is the org-scoped flag
        // hydrated from me/permissions; `organizationId` is the org it resolved — both
        // fall back to the flat session claims while hydration hasn't landed.
        const isGlobalOrgAdmin =
            hasAnyRole(userRoles, [...PLATFORM_STAFF_ROLES]) ||
            ((user?.isAdmin ?? hasAnyRole(userRoles, [...ORG_ADMIN_AREA_ROLES])) &&
                isSystemOrg(user?.organizationId || user?.org_id));

        const navItems = localizeNavTitles(
            filterNavByFeatureFlags(
                isPureSuperAdmin
                    ? buildPhotoflowAdminFallbackNav()
                    : [
                          ...buildPhotoflowOrgFallbackNav(
                              userRoles,
                              user?.isAdmin,
                              user?.organizationId || user?.org_id
                          ),
                          ...buildPhotoQaCentralFallbackNav(
                              userRoles,
                              isGlobalOrgAdmin
                          ),
                      ],
                (flag) => this._flags.isEnabled(flag)
            ),
            (key) => this._transloco.translate(key)
        );

        const navigation: Navigation = {
            compact: navItems,
            default: navItems,
            futuristic: navItems,
            horizontal: navItems,
        };
        this._navigation.next(navigation);
        return of(navigation);
    }
}
